import { Head, Link } from "@inertiajs/react";
import {
  Activity,
  ArrowUpRight,
  BarChart3,
  CalendarCheck,
  ExternalLink,
  Flame,
  Image as ImageIcon,
  LineChart,
} from "lucide-react";

import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const HIGHLIGHT_STATS = [
  { label: "Monthly organic visits", value: "58.5M", icon: Activity },
  { label: "Projected click lift", value: "3× increase", icon: Flame },
  { label: "Revenue opportunity", value: "$117M / month", icon: BarChart3 },
];

const OG_IMAGE_EXAMPLES = [
  {
    title: 'Nike "Why Do It?" OG',
    description:
      "Bold campaign image for Nike’s “Why Do It?” push—on-brand typography, eye-catching gradients, and logo hero make every share a campaign asset.",
    imageUrl: "/case-studies/nike/day-8-nike-after.png",
  },
  {
    title: "Nike x SKIMS Shine Leggings",
    description:
      "Product-focused OG for the SKIMS collab: motion-cued background, athlete product photography, and collection callout for multi-channel reach.",
    imageUrl: "/case-studies/nike/day-8-nike-leggings-woman-after.png",
  },
  {
    title: "Jordan—Starting from $200.00",
    description:
      "OG image spotlights Jordan drop: clear model, price highlight, and aspirational visual narrative to connect with collectors and fans.",
    imageUrl: "/case-studies/nike/day-8-nike-jordan-after.png",
  },
];

const REMAKE_PILLARS = [
  {
    title: "Campaign-consistent storytelling",
    description:
      "Married Nike’s “Why Do It?” message with product photography, layered gradients, and microcopy so every share continues the story.",
  },
  {
    title: "Dynamic metadata injection",
    description:
      "Mapped product titles, pricing, and availability into OG Pilot variables so each SKU renders with accurate context automatically.",
  },
  {
    title: "Platform-optimized exports",
    description:
      "Delivered variations for X, LinkedIn, Threads, and Facebook—each resized and reframed to avoid cropping issues or truncated copy.",
  },
];

export default function NikeCaseStudy() {
  return (
    <>
      <Head title="Nike OG Image Remake Case Study" />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100/40 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="container mx-auto max-w-6xl px-4 pt-10 pb-24 sm:px-6 lg:px-8">
          <PublicHeader />

          <article className="mt-16 space-y-16">
            <header className="relative overflow-hidden rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-orange-200/60 via-transparent to-transparent"
                aria-hidden="true"
              />
              <div className="relative grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                <div className="space-y-6">
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    Case Study · Nike online store
                  </Badge>
                  <h1 className="text-4xl font-bold text-gray-900 md:text-5xl dark:text-white">
                    How refreshed Nike OG images could unlock a projected $117M in monthly revenue
                  </h1>
                  <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                    Day #8 of <span className="font-medium">#100DaysOfOGRemakes</span> focused on Nike’s “Why Do It?”
                    campaign. These OG Pilot mockups explore how refreshed social previews could align every share with
                    campaign storytelling, channel-specific sizing, and data-backed CTAs that inspire 3× more clicks.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm dark:bg-gray-800">
                      <CalendarCheck className="h-4 w-4 text-orange-500" />
                      Published:{" "}
                      <a
                        href="https://x.com/RaulOnRails/status/1977736857963360267"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-700 hover:underline dark:text-orange-300"
                      >
                        Day 8 · #100DaysOfOGRemakes
                      </a>
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-sm dark:bg-gray-800">
                      <LineChart className="h-4 w-4 text-orange-500" />
                      CTR goal: 3× more clicks from organic shares
                    </span>
                  </div>
                </div>

                <div className="grid gap-4 rounded-3xl border border-orange-200/60 bg-orange-100/60 p-6 shadow-inner dark:border-orange-500/30 dark:bg-orange-500/10">
                  {HIGHLIGHT_STATS.map(({ label, value, icon: Icon }) => (
                    <div
                      key={label}
                      className="flex items-start gap-4 rounded-2xl bg-white/90 p-4 shadow dark:bg-gray-950/70"
                    >
                      <div className="rounded-xl bg-orange-500/10 p-3 text-orange-600 dark:bg-orange-500/20 dark:text-orange-200">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                          {label}
                        </p>
                        <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </header>

            <section className="grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl md:grid-cols-[0.85fr_1.15fr] md:items-center dark:bg-gray-900/90">
              <div className="space-y-5">
                <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                  Baseline snapshot
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  The problem: generic previews hiding a flagship campaign
                </h2>
                <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  Nike’s flagship “Why Do It?” rollout leaned on vibrant hero creative, athlete storytelling, and sharp
                  CTAs. Yet when those pages were shared, social crawlers received low-quality or missing OG
                  images—stripping context, killing curiosity, and burying a message that deserved world-stage energy.
                </p>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <li>• Product and collection URLs defaulted to bland thumbnails.</li>
                  <li>
                    • Campaign copy failed to make it into social descriptions, forcing audiences to guess the “Why Do
                    It?” narrative.
                  </li>
                  <li>• No per-channel variants, so previews cropped awkwardly and hid Nike’s strongest imagery.</li>
                </ul>
              </div>
              <Link
                href="https://x.com/RaulOnRails/status/1977736857963360267"
                target="_blank"
                rel="noopener noreferrer"
                className="block transition-transform hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <blockquote className="rounded-3xl border border-orange-200/70 bg-orange-100/60 p-8 text-lg font-medium text-orange-800 shadow-inner dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-100">
                  <p>
                    “Nike needs no intro. Everyone knows the swoosh, but few know it could mean 3× more clicks with
                    better OG images… I remade their previews with OG Pilot to transform social shares into traffic
                    magnets.”
                  </p>
                  <footer className="mt-4 text-sm text-orange-700/80 dark:text-orange-200/80">
                    — Day #8 · #100DaysOfOGRemakes
                  </footer>
                </blockquote>
              </Link>
            </section>

            <section className="rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-6">
                <div>
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    OG image gallery
                  </Badge>
                  <h2 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    Download the concept Nike OG Pilot images
                  </h2>
                  <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    Every variation below was generated inside OG Pilot as part of this exploratory remake. Swap in your
                    own campaign assets, or duplicate the template to create an always-on Nike playbook that content,
                    growth, and retail teams could reuse.
                  </p>
                </div>
              </div>
              <div className="grid gap-8 md:grid-cols-3">
                {OG_IMAGE_EXAMPLES.map((item) => (
                  <div
                    key={item.title}
                    className="flex flex-col gap-4 rounded-3xl border border-orange-200/60 bg-white/90 p-4 shadow-sm dark:border-orange-500/30 dark:bg-gray-950/70"
                  >
                    <div className="overflow-hidden rounded-2xl border border-orange-200/40 bg-gray-100 shadow-inner dark:border-orange-500/20 dark:bg-gray-900/80">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="h-auto w-full rounded-2xl object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{item.title}</h3>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{item.description}</p>
                    </div>
                    <a
                      href={item.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 transition-colors hover:text-orange-700 dark:text-orange-300 dark:hover:text-orange-200"
                    >
                      Open image <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl bg-white/95 p-10 shadow-xl dark:bg-gray-900/90">
              <div className="mb-8 flex flex-wrap items-start justify-between gap-6">
                <div className="max-w-3xl space-y-4">
                  <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                    The OG Pilot remake
                  </Badge>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                    What could change if OG Pilot powered the previews
                  </h2>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                    By pointing OG Pilot at Nike.com, we simulated how the “Why Do It?” stylescape can feed a template
                    system that injects campaign visuals automatically. In this concept, each social share would carry
                    momentum from homepage hero to product detail page without slowing down creative teams.
                  </p>
                </div>
                <Button
                  asChild
                  variant="outline"
                  className="border-orange-300 text-orange-600 hover:border-orange-400 hover:bg-orange-50 dark:border-orange-500/40 dark:text-orange-200 dark:hover:border-orange-400 dark:hover:bg-orange-500/10"
                >
                  <Link href="/sign_up">
                    Try OG Pilot for your next campaign
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                {REMAKE_PILLARS.map((pillar) => (
                  <div
                    key={pillar.title}
                    className="rounded-2xl border border-orange-200/60 bg-white/90 p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-orange-500/30 dark:bg-gray-950/80"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{pillar.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl lg:grid-cols-2 dark:bg-gray-900/90">
              <div className="space-y-5">
                <Badge className="bg-orange-500/10 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200">
                  Revenue impact modeling
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  The math behind Nike’s $117M opportunity
                </h2>
                <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300">
                  Nike already drives <strong>58.5M monthly organic visits</strong>. Matching that reach with
                  irresistible OG images multiplies clicks from every share:
                </p>
                <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
                  <li>• 58.5M visits × 3× lift = 117M additional clicks (modeled)</li>
                  <li>• 1% conversion rate → 1.17M potential incremental orders</li>
                  <li>
                    • $100 average order value → <strong>$117M projected monthly revenue impact</strong>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Sharpening OG images isn’t just aesthetics—it can be a direct-response lever that turns Nike’s brand
                  equity into measurable commerce outcomes when paired with industry benchmark conversion data.
                </p>
              </div>
              <div className="flex h-full flex-col justify-center rounded-3xl border border-orange-200/60 bg-gradient-to-br from-orange-100 via-white to-orange-50 p-8 shadow-inner dark:border-orange-500/30 dark:from-orange-500/20 dark:via-gray-950 dark:to-orange-500/10">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Share this data with your leadership team
                </h3>
                <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
                  OG Pilot automatically tracks per-channel clicks and conversions. Export dashboards into pitch decks
                  or client QBRs to prove the ROI of refreshed OG imagery without manual spreadsheets.
                </p>
              </div>
            </section>

            <section className="overflow-hidden rounded-3xl bg-gradient-to-r from-orange-500 via-orange-600 to-rose-500 p-10 text-white shadow-xl">
              <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-center">
                <div className="space-y-4">
                  <h2 className="text-3xl font-bold">Turn your next share into a Nike-level traffic magnet</h2>
                  <p className="text-sm leading-6 text-orange-50">
                    Plug your domain into OG Pilot, auto-generate branded OG images, and ship them across every channel.
                    No more designer bottlenecks, no more underperforming previews.
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                      <ImageIcon className="h-4 w-4" />
                      Unlimited refinements
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2">
                      <BarChart3 className="h-4 w-4" />
                      Built-in analytics
                    </span>
                  </div>
                </div>
                <div className="rounded-3xl bg-white/10 p-8 backdrop-blur">
                  <h3 className="text-xl font-semibold">Ready to try OG Pilot?</h3>
                  <p className="mt-3 text-sm text-orange-50">
                    Start your free trial, import your brand system, and publish OG images that earn their clicks.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button asChild size="lg" className="bg-white text-orange-600 hover:bg-orange-50">
                      <Link href="/sign_up">Start generating images</Link>
                    </Button>
                    <Button
                      asChild
                      size="lg"
                      variant="outline"
                      className="border-white/60 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/#pricing">View agency plans</Link>
                    </Button>
                  </div>
                  <p className="mt-4 text-xs text-orange-100/90">
                    Framework guides:{" "}
                    <Link href="/nextjs-og-image-generator" className="underline hover:text-white">
                      Next.js
                    </Link>
                    {" · "}
                    <Link href="/rails-og-image-generator" className="underline hover:text-white">
                      Rails
                    </Link>
                    {" · "}
                    <Link href="/astro-og-image-generator" className="underline hover:text-white">
                      Astro
                    </Link>
                  </p>
                </div>
              </div>
            </section>
          </article>
        </div>
      </div>
      <SiteFooter />
    </>
  );
}
